/**
 * React Query hooks for the tenant's OWN interaction vocabulary.
 *
 * An admin-defined kind of logged activity ("kit delivered", "showroom visit"),
 * resolved beside the built-in call / meeting / email / note / task rather than added
 * to them. The admin manager uses the write hooks; the log dialog's picker and the
 * Activity tab use the read hook.
 *
 * Mirrors `useRelationshipClasses` deliberately — the two vocabularies are the same
 * shape and an admin who has met one has met both.
 */
import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ModuleContext } from '../../../../shared/contexts/ModuleContext';

import {
  createInteractionTypeApiV1MasterDataContactsInteractionTypesPost,
  deleteInteractionTypeApiV1MasterDataContactsInteractionTypesTypeIdDelete,
  listInteractionTypesApiV1MasterDataContactsInteractionTypesGet,
  updateInteractionTypeApiV1MasterDataContactsInteractionTypesTypeIdPut,
} from '../../../../generated/api/contacts-interactions/contacts-interactions';
import type { InteractionType } from '../../../../generated/api/models/interactionType';
import type { InteractionTypeCreate } from '../../../../generated/api/models/interactionTypeCreate';
import type { InteractionTypeUpdate } from '../../../../generated/api/models/interactionTypeUpdate';

export type { InteractionType };

/**
 * Where a tenant admin defines interaction types.
 *
 * Spelled once, here, because the log dialog's picker links to it and a hardcoded
 * internal link must name a route `App.tsx` really registers
 * (`.claude/rules/rules-frontend.md`) — two hand-typed copies is how one goes stale.
 */
export const CONTACTS_MODULE_SETTINGS_PATH = '/admin/module-settings/contacts';

export const interactionTypeKeys = {
  all: ['interaction-types'] as const,
  list: (includeInactive: boolean) => ['interaction-types', { includeInactive }] as const,
};

export interface InteractionTypeList {
  types: InteractionType[];
  /** The backend's soft cap on ACTIVE types, carried on the response so this side
   *  never re-spells the number. */
  activeLimit: number;
}

/**
 * The tenant's vocabulary.
 *
 * **Every surface that RENDERS a logged interaction must pass
 * `{ includeInactive: true }`.** A retired type still labels the rows already logged
 * under it, and an active-only fetch cannot be un-filtered client-side: the value is
 * on the row, the option to label it is not, and the badge renders the raw token while
 * nothing errors. Same trap `selectableRelationshipClasses` documents
 * (`.claude/rules/rules-frontend.md` § "A picker that can REMOVE must fetch the
 * retired options too"). Only the CREATE picker wants the active-only list.
 */
export function useInteractionTypes(options?: { includeInactive?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;
  // The route rides master data's own module guard, and the interaction log is a
  // SHARED component: it renders on a pipeline deal too, where a Pipeline-only tenant
  // has master_data switched off and this request would 403. Not fetching is the right
  // answer there rather than swallowing the error — such a tenant has no vocabulary to
  // show, and the log falls back to the built-ins by itself.
  //
  // Read through the CONTEXT rather than `useModule()`, which throws when no provider
  // is mounted. This hook sits under a shared component with four hosts across two
  // modules, so a hard throw here would make every one of them unmountable outside the
  // app shell — a fragility introduced by a purely defensive check. No provider means
  // the question cannot be answered, and the answer that preserves the pre-existing
  // behaviour is to fetch: a failed request already degrades to the built-ins.
  const moduleContext = useContext(ModuleContext);
  const enabled = moduleContext ? moduleContext.isModuleEnabledForUser('master_data') : true;

  return useQuery({
    queryKey: interactionTypeKeys.list(includeInactive),
    enabled,
    queryFn: async (): Promise<InteractionTypeList> => {
      const res = await listInteractionTypesApiV1MasterDataContactsInteractionTypesGet({
        include_inactive: includeInactive,
      });
      return { types: res.data, activeLimit: res.active_limit };
    },
  });
}

/** Create a type, resolving to the CREATED row rather than the transport envelope. */
export function useCreateInteractionType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InteractionTypeCreate) => {
      const res = await createInteractionTypeApiV1MasterDataContactsInteractionTypesPost(data);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: interactionTypeKeys.all }),
  });
}

export function useUpdateInteractionType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ typeId, data }: { typeId: string; data: InteractionTypeUpdate }) =>
      updateInteractionTypeApiV1MasterDataContactsInteractionTypesTypeIdPut(typeId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: interactionTypeKeys.all }),
  });
}

export function useDeleteInteractionType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (typeId: string) =>
      deleteInteractionTypeApiV1MasterDataContactsInteractionTypesTypeIdDelete(typeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: interactionTypeKeys.all }),
  });
}

/**
 * The types a picker may OFFER: every active one, plus the one this row already has.
 *
 * An active-only option list is a one-way door. `is_active: false` means "cannot be
 * used for NEW activity, but everything logged under it is kept" — and a picker that
 * drops the retired type strands exactly that row: the value is on the interaction, the
 * option to label it is not, so the trigger renders EMPTY while the type is still
 * stored and still rendering on the timeline. Nothing errors, which is why the sibling
 * relationship-class picker shipped with this bug twice.
 *
 * Callers pass the id they currently hold and fetch with `{ includeInactive: true }` —
 * this filter cannot re-add a type the request never returned.
 * See `.claude/rules/rules-frontend.md` § "A picker that can REMOVE must fetch the
 * retired options too".
 */
export function selectableInteractionTypes(
  types: InteractionType[] | undefined,
  selectedId: string | null
): InteractionType[] {
  return (types ?? []).filter((type) => type.is_active || type.id === selectedId);
}

/** Resolve a type's display label for the active language (falls back to `label`). */
export function resolveInteractionTypeLabel(type: InteractionType, lang: string): string {
  const short = (lang || 'en').split('-')[0];
  return type.label_translations?.[short] || type.label_translations?.[lang] || type.label;
}

/**
 * Index the vocabulary by id, for a timeline resolving many rows at once.
 *
 * Built once per render of the log rather than searched per row: an Activity tab
 * renders a page of interactions and a `.find()` inside that map is O(rows × types).
 */
export function indexInteractionTypes(
  types: InteractionType[] | undefined
): Map<string, InteractionType> {
  return new Map((types ?? []).map((t) => [t.id, t] as const));
}
